import React from 'react';

import { Header } from './components/br24/header/Header';
import { Date } from './components/br24/date/Date';
import { Authors } from './components/br24/authors/Authors';
import { Quote } from './components/custom/quote/Quote';
import { Position } from './config/models';
import { MobileFoldable } from './components/br24/foldable/Foldable';
import { mainConfig } from './config/config.provider';
import { Teaser } from './components/br24/relatedContent/RelatedContent';
import { RelatedContent } from './components/br24/relatedContent/styles.RelatedContent';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import { LazyImage } from './components/custom/lazyImage/LazyImage';
import { Paper } from './components/custom/paper/Paper';
import { InfoBox } from './components/custom/infoBox/InfoBox';
import { Article, Section, Block, H1, H2, P, A, Strong, List, ListItem } from './styles/styledComponents';
import { changeLang } from './utils/helper/helpers';
import { Scrolly, Slide } from './interactives/scrolly/Scrolly';
import { Main3DSzene } from './interactives/3DSzene/Main';
import { boardConfig } from './config/boardConfig';
import { Empty } from './components/custom/scrollyteller/styles.ScrollyTeller';

import ImageScanner from './assets/images/scanner.jpg';
import ImageMatthias from './assets/images/matthias.jpg';
import ImageMatthiasLaptop from './assets/images/matthias-laptop.jpg';
import ImageOriginalScreen from './assets/images/original-screen.png';
import ImageSaid from './assets/images/said.jpg';

export const ContentEn = () => {
    return (
        <Article id="main-article">
            <Main3DSzene />
            <Header />
            <MobileFoldable triggerText={mainConfig.headerInfo}>{mainConfig.headerCredits}</MobileFoldable>

            <Section id="initial-section">
                <Block isOverlay={mainConfig.headerOverlay}>
                    <Date day={27} month={12} year={2022} time="06:00" />
                    {!mainConfig.titleInHeader ? <H1>{mainConfig.title}</H1> : null}
                    <P>
                        <Strong>{mainConfig.headline}</Strong>
                    </P>
                    <Authors prefix="By" />
                    <List>
                        {mainConfig.provideEn ? (
                            <ListItem>
                                Die deutsche Version dieses Artikels finden Sie <A onClick={changeLang}>hier</A>
                            </ListItem>
                        ) : null}
                        <ListItem>
                            Zum ausführlichen{' '}
                            <A
                                href="https://www.ardaudiothek.de/sendung/das-ard-radiofeature/72633432/"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                ARD-Radiofeature
                            </A>{' '}
                        </ListItem>
                    </List>
                    <P>
                        U.S. troops and their allies collected the biometric data of millions of people in Afghanistan
                        using thousands of biometric scanners. During the chaotic withdrawal of the international troops
                        in August 2021, many of those devices were apparently left behind. Experts have long feared that
                        the Taliban could potentially use those devices to identify people on whom they wish to take
                        revenge. But it had never been proven that such a thing was actually possible. Until now.
                    </P>
                </Block>
            </Section>

            <Scrolly skipLazyLoad={true}>
                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        camera: boardConfig.cameraConfigs['scanner'],
                        transform: [boardConfig.assetTransformations['scanner']['initial']],

                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_home.jpg' }
                    }}
                >
                    The devices that could represent a danger to persecuted Afghans don’t look particularly menacing:
                    Black, 13 centimeters wide and 20 centimeters long, outfitted with a display screen and several
                    cameras.
                </Slide>
                <Empty
                    trigger={{
                        fade: ['human', 'head'],
                        animate: [{ name: 'display_coverAction.001' }]
                    }}
                />
                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        camera: boardConfig.cameraConfigs['finger'],
                        transform: [boardConfig.assetTransformations['scanner']['finger']],
                        animate: [{ name: 'fingerprint_coverAction' }, { name: 'humanAction' }]
                    }}
                >
                    All 10 of a person’s fingerprints can be saved on the devices when each finger is pressed one after
                    the other on a glass sensor plate on the top of the device.
                </Slide>
                <Empty
                    trigger={{
                        camera: boardConfig.cameraConfigs['display_finger'],
                        changeMaterial: {
                            meshId: 'display',
                            materialName: 'display/scanner_display_fingerprint.jpg'
                        }
                    }}
                />
                <Slide
                    position={Position.LEFT}
                    trigger={{
                        camera: boardConfig.cameraConfigs['picture'],
                        transform: [boardConfig.assetTransformations['scanner']['picture']],
                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_photo.jpg' }
                    }}
                >
                    A built-in camera can take biometric facial images.
                </Slide>

                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        camera: boardConfig.cameraConfigs['eyes'],
                        animate: [{ name: 'humanAction', action: 'reverse' }],
                        transform: [boardConfig.assetTransformations['scanner']['eyes']]
                    }}
                >
                    If the front of the device is held up to the eyes, an additional camera scans the pattern of the
                    iris. That pattern is unique to each individual.
                </Slide>
                <Empty
                    trigger={{
                        camera: boardConfig.cameraConfigs['display_eyes'],
                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_iris.jpg' }
                    }}
                />
                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_database.jpg' }
                    }}
                >
                    The biometric data is saved on the device.
                </Slide>
                <Slide
                    position={Position.LEFT}
                    trigger={{
                        camera: boardConfig.cameraConfigs['keyboard_eyes'],

                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_photo.jpg' }
                    }}
                >
                    Additional information can be added using the keyboard, such as date of birth, height, nationality
                    and the reason the person’s biometric data was recorded. NATO troops used the devices to collect and
                    store a huge amount of information.
                </Slide>

                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        camera: boardConfig.cameraConfigs['info'],
                        transform: [boardConfig.assetTransformations['scanner']['initial']],
                        fade: ['info'],
                        changeMaterial: {
                            meshId: 'display',
                            materialName: 'display/scanner_display_identification.jpg'
                        }
                    }}
                >
                    If the same person is later scanned (eyes, fingers or face), the device recognizes them and shows
                    all data pertaining to that person on the display screen.
                </Slide>
                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        camera: boardConfig.cameraConfigs['scanner'],
                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_persons.jpg' },
                        animate: [
                            { name: 'humanAction', action: 'reverse' },
                            { name: 'fingerprint_coverAction', action: 'reverse' }
                        ],
                        fade: ['info', 'human', 'head']
                    }}
                >
                    In the worst case, the Taliban could use the devices to find people who once worked for the NATO
                    troops, the Afghan police or the Afghan military.
                </Slide>
            </Scrolly>

            <Section>
                <Block>
                    <Quote author="Shir Entezar" position={Position.RIGHT}>
                        Every night and every day they are searching for us. We are under the direct threat of getting
                        killed, tortured or jailed.
                    </Quote>
                    <P>
                        “Every night and every day, in every province in every district, they are searching for former
                        government security forces, and they are searching for staff who helped the Afghan national
                        police and the former government. We are under the direct threat of getting killed, tortured or
                        jailed.” Shir Entezar is in hiding. He used to work for the Germans in Afghanistan as an
                        educator for Afghan police officers, teaching them to read and write. When the Taliban took back
                        power in the country, he had to go underground. By the time he initially got in touch with us,
                        he had been in hiding with his family for nine months – somewhere in Afghanistan, far away from
                        his home.
                    </P>

                    <P>
                        “They are searching for the people who helped the police, especially the people who worked for
                        foreign countries like Germany and the U.S.,” he says. In the past, he might have been able to
                        go underground in a different province where nobody knows him. But in today’s Afghanistan, that
                        is no longer enough. His biometric data, collected as part of his employment with the Germans,
                        could reveal his true identity at any time and in any place. He can’t say for sure whether the
                        Taliban have access to that data. But he fears they do.
                    </P>
                    <H2>Biometric Devices for Sale on Ebay</H2>
                    <P>
                        When we first met with IT expert Matthias Marx in March 2022, he too had heard that the Taliban
                        might have the ability to track people down using the biometric scanners left behind by Western
                        troops. Marx lives in Hamburg and is a member of the Chaos Computer Club, the vast German hacker
                        organization. He told us he has discovered and reported more than 100 serious security gaps. He
                        is particularly interested in the issue of biometrics and had two biometric scanners from the
                        U.S. military sitting in front of him during our meeting.
                    </P>
                    <LazyImage
                        src={ImageMatthias}
                        width={900}
                        height={675}
                        note="IT security expert Matthias Marx with one of the devices. Image: NDR/Svea Eckert"
                    />
                    <P>
                        He had found them on the internet, purchasing them on the auction platform Ebay from shops in
                        the U.S. that sell all kinds of discarded items, such as underwater metal detectors or precision
                        thermometers. Much of it appears to come from old military inventories. By way of technical
                        analysis, Matthias Marx wanted to answer the question that had long been the focus of
                        speculation in reports from Afghanistan: Whether the Taliban can in fact use biometric devices
                        left behind by the international troops to identify specific individuals. So, he developed a
                        plan.
                    </P>
                    <P>
                        A few weeks later, we met in Berlin with a group of IT experts from the Chaos Computer Club to
                        examine the devices. The hackers were looking for answers to a number of questions: Are the
                        devices encrypted? Are they protected by a password? Do they still contain data with which the
                        Taliban could identify people?
                    </P>
                </Block>
            </Section>

            <Scrolly>
                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_login.jpg' },
                        camera: boardConfig.cameraConfigs['keyboard_initial'],
                        fade: ['highlight_key_1', 'highlight_key_2'],
                        animate: [
                            { name: 'highlight_key_1', mode: 'LoopPingPong', repetitions: Infinity },
                            { name: 'highlight_key_2', mode: 'LoopPingPong', repetitions: Infinity }
                        ]
                    }}
                >
                    The IT experts quickly determined that there is no encryption that could protect the data from being
                    accessed by an outsider. The password protection also proved simple to circumvent. As the
                    instruction manual made clear, all you had to do was push a couple of buttons.
                </Slide>

                <Slide
                    position={Position.LEFT}
                    trigger={{
                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_home.jpg' },
                        camera: boardConfig.cameraConfigs['keyboard_initial_close']
                    }}
                >
                    “You could just as well leave out the password,” says Matthias Marx. “The Taliban could use these
                    devices immediately. There are pretty much no hurdles at all.”
                </Slide>

                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        camera: boardConfig.cameraConfigs['display_initial'],
                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_soldier.jpg' },
                        animate: [
                            { name: 'highlight_key_1', action: 'stop' },
                            { name: 'highlight_key_2', action: 'stop' }
                        ]
                    }}
                >
                    At this point in time, the hackers did not find data belonging to people in Afghanistan. But they
                    did find an indication that the devices they examined did, in fact, come from the U.S. military.
                </Slide>

                <Slide position={Position.LEFT} trigger={{}}>
                    On one of the devices, they found the names, photos, fingerprints and iris scans of two American
                    soldiers. One is apparently an intelligence officer in the U.S. Marine Corps.
                </Slide>

                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_jordan.jpg' },
                        camera: boardConfig.cameraConfigs['display_initial_close']
                    }}
                >
                    The hackers also found something else: Coordinates that correspond to a place in Jordan.
                </Slide>
                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        camera: boardConfig.cameraConfigs['display_initial_close'],
                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_connection.jpg' }
                    }}
                >
                    At the end of the examination, the hackers found another surprise: The device tried to establish an
                    internet connection – apparently to the central biometrics database of the U.S. Defense Department.
                </Slide>
                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        camera: boardConfig.cameraConfigs['data'],
                        fade: ['server_1', 'laptop']
                    }}
                >
                    The servers for the “Automated Biometric Information System” (ABIS) are located not in Afghanistan,
                    but in the U.S. state of West Virginia.
                </Slide>
                <Slide position={Position.RIGHT} trigger={{ drawLines: [{ source: 'scanner', target: 'server_1' }] }}>
                    In the field the devices regularly connect with this database and upload all the data they have
                    collected.
                </Slide>
                <Slide position={Position.RIGHT} trigger={{ camera: boardConfig.cameraConfigs['server'] }}>
                    ABIS contains the “identities” of millions of people whose data has been collected by the U.S.
                    military during missions around the world.
                </Slide>
                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        changeMaterial: {
                            meshId: 'server_display',
                            materialName: 'display/server_display_terrorist.jpg'
                        }
                    }}
                >
                    The entries also include threat levels. If, for example, the threat level 1 (“detain”) is entered,
                    that person has been identified as dangerous and should be taken into custody. A threat level of 2
                    (“question”) indicates that the person should be interrogated and level 4 (“disqualify”) means that
                    the person should be denied access to military bases.
                </Slide>
                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        camera: boardConfig.cameraConfigs['server_data_close'],
                        drawLines: [{ source: 'server_1', target: 'scanner' }]
                    }}
                >
                    Every few days, U.S. soldiers were required to download long lists containing such information to
                    the devices. The reason: internet access in Afghanistan was frequently too poor to establish a
                    reliable connection with ABIS in the U.S.
                </Slide>
                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        camera: boardConfig.cameraConfigs['server_data'],
                        fade: ['server_2', 'server_3'],
                        drawLines: [
                            { source: 'server_1', target: 'server_2' },
                            { source: 'server_3', target: 'server_1' },
                            { source: 'server_2', target: 'server_3' }
                        ]
                    }}
                >
                    The data that ended up on the devices through the downloads may have come not only from ABIS, but
                    also from databases kept by the FBI, the Department of Homeland Security and the Department of
                    Justice, all of which are linked to ABIS.
                </Slide>
            </Scrolly>
            <Section>
                <Block>
                    <P>
                        At the initiative of the U.S., the international troops under NATO leadership pursued the goal
                        of collecting biometric data from the entire population of Afghanistan. The strategy was known
                        as “Identity Dominance.” According to a U.S. military biometrics handbook, the idea behind it
                        was, the faster the biometric data of the entire population could be collected, the faster all
                        attackers could be removed from the battlefield. To achieve that goal, U.S. soldiers in
                        Afghanistan deployed several thousand such devices and scanned millions of people. German
                        soldiers were also involved in collecting biometric data in Afghanistan.
                    </P>

                    <LazyImage
                        src={ImageScanner}
                        width={900}
                        height={1200}
                        note="One of the biometric devices analysed by Matthias Marx. Image: BR/Rebecca Ciesielski"
                    />

                    <P>
                        Biometric scanning became an omnipresent fact of life in Afghanistan in recent years. When
                        voters cast their ballots, when they sought access to military bases, when they wanted to work
                        for the police or in the schools: In all such cases, their identity was checked and their
                        biometric data saved. A former senior official in the Afghan Interior Ministry confirmed for us
                        that a great deal of this data found its way to ABIS in the U.S. Including data that wasn’t
                        collected by international troops, but by staff members of Afghan government agencies. Which
                        makes it possible that Shir Entezar’s data is also stored in ABIS. Shir Entezar himself believes
                        it is quite likely. If that data was to find its way back to the biometric devices still in
                        Afghanistan and, thus, to the Taliban, his family would be in great danger.
                    </P>
                    <H2>Shir Entezar’s Family Has Been in Hiding for Months</H2>

                    <P>
                        When we again spoke with Shir Entezar, he, his wife and his children had once again moved to a
                        different hiding place. In autumn 2022, their situation seemed to be growing increasingly
                        threatening. By this point, he and his family had been trying to get out of Afghanistan for over
                        a year. Through a German NGO, Shir Entezar had applied to the German government to be allowed to
                        emigrate, but he had heard nothing for several months. “I am waiting for a positive answer from
                        Germany to save my life,” he told us.
                    </P>
                    <H2>Countless Photos, Iris Scans and Fingerprints</H2>

                    <P>
                        Meanwhile, Matthias Marx had bought more biometric scanners on Ebay and had now managed to
                        analyze a total of five of them. He made a spectacular find on one: Thousands of secret U.S.
                        military files. He had copied the entire content of the device onto his laptop and showed us
                        photos of people staring into the camera, not looking particularly happy. Most had dark hair,
                        many wore a beard and some were dressed in orange overalls of the kind familiar from Guantanamo.
                    </P>
                </Block>
            </Section>
            <Scrolly>
                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        camera: boardConfig.cameraConfigs['hacker_info_close'],
                        fade: ['hacker_info'],
                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_info.jpg' }
                    }}
                >
                    Matthias Marx finds a large database on one of the devices full of information. Data from 2,600
                    people, from the U.S. missions in Afghanistan and Iraq. The data includes thousands of iris scans,
                    fingerprints and photos.
                </Slide>
                <Slide
                    position={Position.RIGHT}
                    trigger={{ camera: boardConfig.cameraConfigs['info'], fade: ['human', 'head', 'info'] }}
                >
                    In addition to the biometric information, the devices also include information about the people
                    whose data was collected, including height, weight, birthdays and much more.
                </Slide>
                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        fade: ['info_description_terrorist'],
                        camera: boardConfig.cameraConfigs['info_close'],
                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_terrorist.jpg' }
                    }}
                >
                    Some of the data apparently belongs to wanted terrorists who are on a U.S. Defense Department list.
                    Other data appears to have been collected by soldiers in the field using the device itself, at a
                    U.S. military roadblock, for example.
                </Slide>
                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        fade: ['info_description_terrorist', 'info', 'info_national_police', 'info_description_police'],
                        camera: boardConfig.cameraConfigs['info_close'],
                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_profiles.jpg' }
                    }}
                >
                    One finding is particularly sensitive: Some of the people in the data are clearly identified as
                    volunteers – as former members of the police or military. That status is indicated by entries like
                    “Volunteer Background Checks,” “ECP Base Access,” “National Security Forces Screening,” “Host Nation
                    Police,” or “Host Nation Military.”
                </Slide>
                <Slide
                    position={Position.RIGHT}
                    trigger={{
                        camera: boardConfig.cameraConfigs['hacker_info_close'],
                        fade: ['info_description_last_usage'],
                        changeMaterial: { meshId: 'display', materialName: 'display/scanner_display_afghanistan.jpg' }
                    }}
                >
                    According to GPS coordinates found on the device, it was deployed in 2012 in the southern
                    Afghanistan province of Zabul.
                </Slide>
            </Scrolly>

            <Section>
                <Block>
                    <H2>The Evidence: The Devices Put People's Lives at Risk</H2>
                    <P>
                        This information found on the biometric scanner could be sufficient to put someone’s life at
                        risk. If the Taliban had possession of such a device, they would be able to make a definitive
                        determination as to whether people were supporters of the former Afghan government or the
                        international troops. As people against whom they want to take revenge. That was what Matthias
                        Marx had been looking for, and it represents conclusive proof of just how dangerous these
                        devices are that were deployed and left behind by the U.S. and by other international troops
                        under NATO leadership.
                    </P>
                    <LazyImage
                        src={ImageMatthiasLaptop}
                        width={900}
                        height={675}
                        note="Matthias Marx shows facial images on his laptop. Image: NDR/Svea Eckert"
                    />
                    <P>
                        “The U.S. government in this case has to be held accountable,” said Belkis Wille, when we told
                        her about Marx’s discovery. Wille is a crisis and conflict researcher at the NGO Human Rights
                        Watch and wrote a report in which she warned of the dangers represented by the biometrics
                        devices. Of the new findings, she said: “Now we realize that these systems were in and of
                        themselves fundamentally insecure. They were not fit for the purpose.” Given the security
                        situation in Afghanistan, she says, the deployment of such devices was “highly reckless.” She
                        demands that the U.S. government take immediate action and that endangered people should be
                        given the possibility to leave Afghanistan and apply for asylum. “Biometric signatures are
                        things that you can’t erase. They stay with you forever,” she said. “Even a former policeman who
                        is in hiding, who has changed their name because they don't want the Taliban to capture them
                        isn’t safe anymore. This system means that they really have no way to protect themselves.”
                    </P>
                    <LazyImage
                        src={ImageOriginalScreen}
                        width={800}
                        height={480}
                        note="Original screenshot of one device showing categories like “Host Nation Military”"
                    />
                    <H2>Secret Deal on the Handling of Data</H2>

                    <P>
                        Some of the data shows that aside from the U.S., other troops active in Afghanistan also
                        collected biometric data. In one field, for example, it says “UK” along with a British telephone
                        number. Another says “Canadian.” One of the data files includes the abbreviation “GER.” Could
                        data collected by the German military have ended up on the device? Years ago, Wikileaks
                        published an agreement that had originally been secret, a deal between the U.S. Department of
                        Defense and the German Defense Ministry regulating the manner in which the U.S. handles data
                        collected by the German military, the Bundeswehr, in Afghanistan. According to the deal, the
                        data was to be “stored in a technical environment that is safeguarded against unauthorized
                        access” and deleted following the end of the Afghanistan mission. “In particular,” it reads:
                    </P>
                    <Paper signature={'Memorandum of Understanding between USA and Germany from 2011'}>
                        “Appropriate measures are to be taken to ensure that Bundeswehr-gathered data is not to be used
                        in connection with the arbitrary detention and prosecution or mistreatment of persons.”
                    </Paper>
                    <P>
                        According to what Matthias Marx has discovered, there is concern that precisely that might
                        happen. And that data collected by NATO troops, including Germans, could put people’s lives at
                        risk.
                    </P>
                    <P>
                        When reached for comment by BR, the German Defense Ministry said they were in possession of no
                        information regarding the issue. The devices used by the German Bundeswehr had been returned to
                        the NATO mission command at the end of the mission, the Defense Ministry said. The U.S. Defense
                        Department said when contacted that the Afghan military had been given 1,200 biometric devices.
                        Initially, these devices had been able to connect with databases in the U.S., but that such a
                        transfer of data was now no longer possible. We were unable to learn how many biometric devices
                        were deployed in Afghanistan, how many of them remained behind and why data can still be found
                        on them. Those questions were not answered by the U.S. Defense Department, NATO and the German
                        government. We were also unable to find out why Matthias Marx had been able to buy some of these
                        devices on Ebay.
                    </P>
                    <LazyImage
                        src={ImageSaid}
                        width={900}
                        height={675}
                        note="Shir Entezar in December 2022 in Germany. Image: BR/Rebecca Ciesielski"
                    />
                    <H2>Shir Entezar May Leave – But Many Others Remain Stuck</H2>
                    <P>
                        In November 2022, Shir Entezar got in touch with us again. After several months of waiting, he
                        had finally been granted permission to relocate to Germany. And just a few weeks after that, he
                        and his family were in safety. But he is apparently an exception. Many of his former colleagues
                        are still in Afghanistan and are afraid for their lives. “Hundreds of my colleagues received
                        rejections. I’m worried about them. They are not safe in Afghanistan and have cases similar to
                        mine.” When we told Shir Entezar about the findings made by Matthias Marx, he grew emotional: “I
                        am so sorry about Germany and about NATO, about the U.S., that they forgot all of their
                        biometric machines in Afghanistan. That they have put the lives of people in Afghanistan at
                        risk.”
                    </P>
                </Block>
                <Block>
                    <InfoBox>
                        <H2>About the project:</H2>
                        <P>
                            This is a project by Bayerischer Rundfunk (BR Data, AI + Automation Lab and BR Recherche).
                        </P>
                        <List>
                            <ListItem>
                                Also the{' '}
                                <A
                                    href="https://www.nytimes.com/2022/12/27/technology/for-sale-on-ebay-a-military-database-of-fingerprints-and-iris-scans.html"
                                    rel="noopener noreferrer"
                                    target="_blank"
                                >
                                    New York Times
                                </A>{' '}
                                is covering the subject.
                            </ListItem>
                            <ListItem>
                                {' '}
                                <A
                                    href="https://www.ccc.de/de/updates/2022/afghanistan-biometrie"
                                    rel="noopener noreferrer"
                                    target="_blank"
                                >
                                    Here
                                </A>{' '}
                                you can find an extensive blog post by Chaos Computer Club.
                            </ListItem>
                        </List>
                        <List>
                            <ListItem>
                                <b>Reporters: </b>
                                {mainConfig.authors.join(', ')}
                            </ListItem>
                            <ListItem>
                                <b>Digital design: </b>Steffen Kühne, Sebastian Bayerl
                            </ListItem>
                            <ListItem>
                                <b>Editors: </b> Robert Schöffel, Helga van Ooijen
                            </ListItem>
                            <ListItem>
                                <b>Translation: </b> Charles Hawley
                            </ListItem>
                        </List>
                    </InfoBox>
                    <P>Published on December 27, 2022</P>
                </Block>

                <Block>
                    <LazyLoadComponent>
                        <H2>Das könnte Sie auch interessieren:</H2>
                        <RelatedContent>
                            <Teaser
                                timestamp="27.12.2022, 08:00 Uhr"
                                title="ARD-Radiofeature: Verräterische Daten"
                                text="Das ausführliche ARD-Radiofeature „Verräterische Daten: Doku über die Gefahren von Biometrie“ in der ARD Audiothek"
                                href="https://www.ardaudiothek.de/sendung/das-ard-radiofeature/72633432/"
                                img={
                                    'https://img.br.de/4ce05d55-248a-41b9-b809-02587a429314.jpeg?rect=0,289,5760,3238&w=1024&q=90'
                                }
                                alt="Das ARD-Radiofeature"
                            />
                            <Teaser
                                timestamp="17.5.2022, 5:46 Uhr"
                                title="Afghanistan: Helfer in Lebensgefahr"
                                text="Die Regierung hat versprochen, gefährdete Personen vor den Taliban in Sicherheit zu bringen. Afghanische Ex-Mitarbeiter der Entwicklungshilfeagentur GIZ fallen aber durchs Raster. BR-Recherchen zeigen: Sie sind in größerer Gefahr als bislang bekannt."
                                href="https://www.br.de/nachrichten/deutschland-welt/afghanistan-deutschlands-helfer-in-lebensgefahr,T61CvtO"
                                img={
                                    'https://img.br.de/648aa322-f616-4654-bed5-19130f2a828d.jpeg?rect=0,289,5760,3238&w=1024&q=90'
                                }
                                alt="BR24 Afghanistan: Deutschlands Helfer in Lebensgefahr"
                            />
                            <Teaser
                                timestamp="27.12.2022, 06:00 Uhr"
                                title="Podcast: She Likes Tech"
                                text="BR-Reporterin Rebecca Ciesielski zu Gast im Tech-Podcast „She Likes Tech“ mit Svea Eckert zum Thema Biometrie in der ARD Audiothek"
                                href="https://www.ardaudiothek.de/sendung/she-likes-tech-der-podcast-ueber-technologie/80042936/"
                                img={'https://www.ndr.de/mediathek/podcast4812_v-contentxl.jpg'}
                                alt="Zur Podcast-Folge von She likes tech"
                            />
                        </RelatedContent>
                    </LazyLoadComponent>
                </Block>
            </Section>
        </Article>
    );
};
